import { useSyncExternalStore } from "react";
import { shortenPath } from "@foreman/app-core";

/**
 * How a computer's card on Home lists its sessions: grouped under the folder each one runs in, or one flat list. The choice is
 * the person's, kept on this phone.
 */
export type HomeView = "folders" | "flat";
export const DEFAULT_HOME_VIEW: HomeView = "folders";
const KEY = "foreman.home.view";

export const parseHomeView = (raw: string | null | undefined): HomeView => (raw === "flat" || raw === "folders" ? raw : DEFAULT_HOME_VIEW);

const listeners = new Set<() => void>();
const read = (): HomeView => {
  try {
    return parseHomeView(localStorage.getItem(KEY));
  } catch {
    return DEFAULT_HOME_VIEW;
  }
};
let cached: HomeView | null = null;
const get = (): HomeView => (cached ??= read());

export function setHomeView(view: HomeView): void {
  cached = parseHomeView(view);
  try {
    localStorage.setItem(KEY, cached);
  } catch {
    /* the choice still holds until the app closes */
  }
  for (const l of [...listeners]) l();
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
export const useHomeView = (): HomeView => useSyncExternalStore(subscribe, get, () => DEFAULT_HOME_VIEW);

export type FolderGroup<T> = { key: string; folder: string; rows: T[] };

/** The same folder written two ways (a trailing slash, `C:\a` and `C:/a`) is one folder. */
export const folderKey = (cwd: string): string => shortenPath(cwd.replace(/[\\/]+$/, "")).replace(/\\/g, "/") || "/";

/** Rows grouped by folder, the folder with the most recent activity first; rows keep the order they came in. */
export function groupByFolder<T extends { session: { cwd: string; lastActivityAt?: number; startedAt: number } }>(rows: T[]): FolderGroup<T>[] {
  const groups = new Map<string, FolderGroup<T> & { at: number }>();
  for (const r of rows) {
    const key = folderKey(r.session.cwd);
    const at = r.session.lastActivityAt ?? r.session.startedAt;
    const g = groups.get(key);
    if (g) (g.rows.push(r), (g.at = Math.max(g.at, at)));
    else groups.set(key, { key, folder: shortenPath(r.session.cwd), rows: [r], at });
  }
  return [...groups.values()].sort((a, b) => b.at - a.at).map(({ at: _at, ...g }) => g);
}
